import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, clearAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, Car, DollarSign, LogOut, Upload, Users, QrCode, Check, X, Edit2, Building2, Shield, Plus, Trash2, ClipboardList, Ban, Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BookingHistoryTab } from '@/components/admin/BookingHistoryTab';
import { BlocklistTab } from '@/components/admin/BlocklistTab';
import { HTTP_FORBIDDEN, APPROVAL_STYLES } from '@/constants';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'residents', label: 'Residents', icon: Users },
  { id: 'guards', label: 'Guards', icon: Shield },
  { id: 'bookings', label: 'Bookings', icon: ClipboardList },
  { id: 'blocklist', label: 'Blocklist', icon: Ban },
  { id: 'upload', label: 'CSV Upload', icon: Upload },
  { id: 'approvals', label: 'Approvals', icon: Check },
  { id: 'registration', label: 'Registration Link', icon: QrCode },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [occupancy, setOccupancy] = useState({ daily_stats: [] });
  const [residents, setResidents] = useState({ residents: [], buildings: [] });
  const [requests, setRequests] = useState([]);
  const [regInfo, setRegInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);
  const [filterBuilding, setFilterBuilding] = useState('all');
  const [guards, setGuards] = useState([]);
  const [showCreateGuard, setShowCreateGuard] = useState(false);
  const [guardForm, setGuardForm] = useState({ phone: '', name: '', password: '' });
  const [showAddResident, setShowAddResident] = useState(false);
  const [residentForm, setResidentForm] = useState({ building_name: '', flat_number: '', owner_name: '', contact_number: '', parking_spots: '' });
  const [editingResident, setEditingResident] = useState(null); // full flat object
  const [editForm, setEditForm] = useState({ owner_name: '', flat_number: '', building_name: '', contact_number: '', parking_spots: '' });
  const [bookingHistory, setBookingHistory] = useState([]);
  const [blockedVisitors, setBlockedVisitors] = useState([]);
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [analyticsRes, occupancyRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/occupancy`)
      ]);
      setAnalytics(analyticsRes.data);
      setOccupancy(occupancyRes.data);

      // Try society-admin endpoints (only works if user has society_id)
      try {
        const [residentsRes, requestsRes, regRes, guardsRes, bookingsRes, blocklistRes] = await Promise.all([
          axios.get(`${API}/society-admin/residents`),
          axios.get(`${API}/society-admin/registration-requests`),
          axios.get(`${API}/society-admin/registration-info`),
          axios.get(`${API}/society-admin/guards`),
          axios.get(`${API}/society-admin/booking-history`),
          axios.get(`${API}/society-admin/blocked-visitors`)
        ]);
        setResidents(residentsRes.data);
        setRequests(requestsRes.data.requests || []);
        setRegInfo(regRes.data);
        setGuards(guardsRes.data.guards || []);
        setBookingHistory(bookingsRes.data.bookings || []);
        setBlockedVisitors(blocklistRes.data.blocked_visitors || []);
      } catch (e) {
        // Admin not assigned to society - expected for unassigned admins
        if (e?.response?.status !== HTTP_FORBIDDEN) {
          toast.error('Failed to load society data');
        }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (!role || role !== 'admin') {
      navigate('/auth/admin');
      return;
    }
    fetchAll();
  }, [navigate, fetchAll]);

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/society-admin/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(res.data);
      toast.success(res.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleEditResident = async (flatId) => {
    const spots = editForm.parking_spots.split(';').map(s => s.trim()).filter(Boolean);
    const payload = {
      owner_name: editForm.owner_name,
      flat_number: editForm.flat_number,
      new_building_name: editForm.building_name,
      contact_number: editForm.contact_number,
      parking_spots: spots
    };
    try {
      await axios.put(`${API}/society-admin/residents/${flatId}`, payload);
      toast.success('Resident updated successfully');
      setEditingResident(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleAddResident = async () => {
    if (!residentForm.building_name || !residentForm.flat_number || !residentForm.owner_name) {
      toast.error('Building, Flat Number, and Name are required');
      return;
    }
    const spots = residentForm.parking_spots.split(';').map(s => s.trim()).filter(Boolean);
    try {
      const res = await axios.post(`${API}/society-admin/create-resident`, {
        building_name: residentForm.building_name,
        flat_number: residentForm.flat_number,
        owner_name: residentForm.owner_name,
        contact_number: residentForm.contact_number,
        parking_spots: spots
      });
      toast.success(res.data.message);
      setResidentForm({ building_name: '', flat_number: '', owner_name: '', contact_number: '', parking_spots: '' });
      setShowAddResident(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add resident');
    }
  };

  const handleApproval = async (userId, action) => {
    try {
      await axios.post(`${API}/society-admin/approve-registration`, { user_id: userId, action });
      toast.success(`Registration ${action}d`);
      fetchAll();
    } catch (error) {
      toast.error('Failed to process');
    }
  };

  const handleCreateGuard = async () => {
    if (!guardForm.phone || !guardForm.name || !guardForm.password) {
      toast.error('All fields required');
      return;
    }
    try {
      await axios.post(`${API}/society-admin/create-guard`, guardForm);
      toast.success('Guard created');
      setGuardForm({ phone: '', name: '', password: '' });
      setShowCreateGuard(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create guard');
    }
  };

  const handleDeleteGuard = async (guardId, guardName) => {
    if (!window.confirm(`Remove guard "${guardName}"? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/society-admin/guards/${guardId}`);
      toast.success(res.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove guard');
    }
  };

  const handleLogout = () => { axios.post(`${API}/auth/logout`).catch(() => {}); clearAuthToken(); localStorage.clear(); navigate('/'); };

  const downloadQR = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBlockVisitor = async (vehicleNumber, phone, reason) => {
    try {
      await axios.post(`${API}/society-admin/block-visitor`,
        { vehicle_number: vehicleNumber || null, phone: phone || null, reason }
      );
      toast.success('Visitor blocked successfully');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block visitor');
    }
  };

  const handleUnblock = async (blockId) => {
    try {
      await axios.post(`${API}/society-admin/unblock-visitor/${blockId}`, {});
      toast.success('Visitor unblocked');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock');
    }
  };

  if (loading || !analytics) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
        <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Loading...</p>
      </div>
    );
  }

  const hasSociety = !!regInfo;
  const filteredResidents = filterBuilding === 'all'
    ? residents.residents
    : residents.residents.filter(r => r.building_id === filterBuilding);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F4F5' }}>
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div style={{ backgroundColor: '#09090B', padding: '12px' }}>
              <BarChart3 size={28} style={{ color: '#FFFFFF' }} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl tracking-tight font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }} data-testid="admin-dashboard-title">
                {regInfo ? regInfo.society_name : 'Society Admin'}
              </h1>
              <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px' }}>
                Society Management Dashboard
              </p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" data-testid="logout-button" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </div>

      {/* Tabs */}
      {hasSociety && (
        <div className="border-b" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
          <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className="flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap"
                style={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: activeTab === tab.id ? '700' : '400',
                  color: activeTab === tab.id ? '#09090B' : '#71717A',
                  borderColor: activeTab === tab.id ? '#FBBF24' : 'transparent'
                }}
              >
                <tab.icon size={16} />
                {tab.label}
                {tab.id === 'approvals' && requests.length > 0 && (
                  <span className="ml-1 w-5 h-5 flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#DC2626', color: '#FFFFFF', borderRadius: '50%' }}>
                    {requests.length}
                  </span>
                )}
                {tab.id === 'blocklist' && blockedVisitors.length > 0 && (
                  <span className="ml-1 w-5 h-5 flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#DC2626', color: '#FFFFFF', borderRadius: '50%' }}>
                    {blockedVisitors.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {!hasSociety && (
          <div className="p-8 border text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
            <Building2 size={48} style={{ color: '#71717A', margin: '0 auto 16px' }} />
            <p className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
              Not Assigned to a Society
            </p>
            <p className="mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
              Contact the platform admin to assign you to a society.
            </p>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Total Bookings', value: analytics.total_bookings, icon: Car, bg: '#FBBF24' },
                { label: 'Active Now', value: analytics.active_bookings, icon: TrendingUp, bg: '#10B981' },
                { label: 'Total Revenue', value: `\u20B9${analytics.total_revenue.toFixed(0)}`, icon: DollarSign, bg: '#09090B' },
                { label: 'Available Spots', value: `${analytics.available_spots}/${analytics.total_spots}`, icon: Car, bg: '#FBBF24' }
              ].map((card) => (
                <div key={card.label} className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid={`metric-${card.label.toLowerCase().replace(' ', '-')}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div style={{ backgroundColor: card.bg, padding: '10px' }}>
                      <card.icon size={20} style={{ color: card.bg === '#09090B' ? '#FFFFFF' : '#09090B' }} />
                    </div>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#71717A' }}>{card.label}</p>
                  </div>
                  <p className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{card.value}</p>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div className="p-6 border mb-8" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
              <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                7-Day Occupancy Trends
              </h2>
              {occupancy.daily_stats.length === 0 ? (
                <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>No data available yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={occupancy.daily_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="date" stroke="#71717A" style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }} />
                    <YAxis stroke="#71717A" style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }} />
                    <Tooltip contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E4E4E7', fontFamily: 'IBM Plex Sans, sans-serif' }} />
                    <Line type="monotone" dataKey="bookings" stroke="#FBBF24" strokeWidth={3} name="Bookings" />
                    <Line type="monotone" dataKey="revenue" stroke="#09090B" strokeWidth={3} name="Revenue" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Revenue Split */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>Booking Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7' }}>
                    <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Active</span>
                    <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#10B981' }}>{analytics.active_bookings}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7' }}>
                    <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Completed</span>
                    <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{analytics.completed_bookings}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>Revenue Split</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3" style={{ backgroundColor: '#FBBF24' }}>
                    <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B', fontWeight: 'bold' }}>Owners (60%)</span>
                    <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{'\u20B9'}{(analytics.total_revenue * 0.6).toFixed(0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3" style={{ backgroundColor: '#F4F4F5' }}>
                    <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Society (30%)</span>
                    <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{'\u20B9'}{(analytics.total_revenue * 0.3).toFixed(0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3" style={{ backgroundColor: '#F4F4F5' }}>
                    <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Platform (10%)</span>
                    <span className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{'\u20B9'}{(analytics.total_revenue * 0.1).toFixed(0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Residents Tab */}
        {activeTab === 'residents' && hasSociety && (
          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                Residents & Parking Spots
              </h2>
              <div className="flex items-center gap-3">
                <Button onClick={() => setShowAddResident(!showAddResident)} data-testid="add-resident-btn" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                  <Plus size={16} className="mr-2" /> Add Resident
                </Button>
                <select
                  value={filterBuilding}
                  onChange={(e) => setFilterBuilding(e.target.value)}
                  data-testid="building-filter"
                  className="border p-2 text-sm"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#E4E4E7' }}
                >
                  <option value="all">All Buildings</option>
                  {residents.buildings.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Add Resident Form */}
            {showAddResident && (
              <div className="mb-6 p-4 border" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }} data-testid="add-resident-form">
                <p className="text-sm font-bold mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>New Resident</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                  <div>
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Tower / Building *</Label>
                    <Input value={residentForm.building_name} onChange={(e) => setResidentForm({ ...residentForm, building_name: e.target.value })} placeholder="e.g. Tower A" data-testid="resident-building-input" className="mt-1" list="building-list" />
                    <datalist id="building-list">
                      {residents.buildings.map(b => <option key={b.id} value={b.name} />)}
                    </datalist>
                  </div>
                  <div>
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Flat Number *</Label>
                    <Input value={residentForm.flat_number} onChange={(e) => setResidentForm({ ...residentForm, flat_number: e.target.value })} placeholder="e.g. A-101" data-testid="resident-flat-input" className="mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                  </div>
                  <div>
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Owner / Tenant Name *</Label>
                    <Input value={residentForm.owner_name} onChange={(e) => setResidentForm({ ...residentForm, owner_name: e.target.value })} placeholder="e.g. Rahul Sharma" data-testid="resident-name-input" className="mt-1" />
                  </div>
                  <div>
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Contact Number</Label>
                    <Input value={residentForm.contact_number} onChange={(e) => setResidentForm({ ...residentForm, contact_number: e.target.value })} placeholder="e.g. 9876543210" data-testid="resident-contact-input" className="mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                  </div>
                  <div className="md:col-span-2">
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Parking Spots (semicolon-separated)</Label>
                    <Input value={residentForm.parking_spots} onChange={(e) => setResidentForm({ ...residentForm, parking_spots: e.target.value })} placeholder="e.g. A-101-P1; A-101-P2" data-testid="resident-spots-input" className="mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddResident} data-testid="save-resident-btn" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                    Add Resident
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddResident(false)}>Cancel</Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F4F4F5' }}>
                    <th className="text-left p-3 font-bold" style={{ color: '#09090B' }}>Building</th>
                    <th className="text-left p-3 font-bold" style={{ color: '#09090B' }}>Flat</th>
                    <th className="text-left p-3 font-bold" style={{ color: '#09090B' }}>Owner/Tenant</th>
                    <th className="text-left p-3 font-bold" style={{ color: '#09090B' }}>Contact</th>
                    <th className="text-left p-3 font-bold" style={{ color: '#09090B' }}>Parking Spots</th>
                    <th className="text-left p-3 font-bold" style={{ color: '#09090B' }}>Registered</th>
                    <th className="text-left p-3 font-bold" style={{ color: '#09090B' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResidents.map((flat) => (
                    <tr key={flat.id} className="border-t" style={{ borderColor: '#E4E4E7' }} data-testid={`resident-row-${flat.flat_number}`}>
                      {editingResident?.id === flat.id ? (
                        <>
                          <td className="p-3">
                            <Input value={editForm.building_name} onChange={(e) => setEditForm({ ...editForm, building_name: e.target.value })} className="h-8 text-sm" style={{ width: '120px' }} data-testid={`edit-building-${flat.flat_number}`} list="edit-building-list" />
                            <datalist id="edit-building-list">
                              {residents.buildings.map(b => <option key={b.id} value={b.name} />)}
                            </datalist>
                          </td>
                          <td className="p-3">
                            <Input value={editForm.flat_number} onChange={(e) => setEditForm({ ...editForm, flat_number: e.target.value })} className="h-8 text-sm" style={{ width: '90px', fontFamily: 'JetBrains Mono, monospace' }} data-testid={`edit-flat-${flat.flat_number}`} />
                          </td>
                          <td className="p-3">
                            <Input value={editForm.owner_name} onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })} className="h-8 text-sm" style={{ width: '140px' }} data-testid={`edit-name-${flat.flat_number}`} />
                          </td>
                          <td className="p-3">
                            <Input value={editForm.contact_number} onChange={(e) => setEditForm({ ...editForm, contact_number: e.target.value })} className="h-8 text-sm" style={{ width: '120px', fontFamily: 'JetBrains Mono, monospace' }} data-testid={`edit-contact-${flat.flat_number}`} />
                          </td>
                          <td className="p-3">
                            <Input value={editForm.parking_spots} onChange={(e) => setEditForm({ ...editForm, parking_spots: e.target.value })} className="h-8 text-sm" style={{ width: '160px', fontFamily: 'JetBrains Mono, monospace' }} data-testid={`edit-spots-${flat.flat_number}`} placeholder="P1; P2" />
                          </td>
                          <td className="p-3">
                            {flat.registered_owner ? (
                              <span className="text-xs px-2 py-0.5" style={{
                                backgroundColor: (APPROVAL_STYLES[flat.registered_owner.approval_status] || APPROVAL_STYLES.rejected).bg,
                                color: (APPROVAL_STYLES[flat.registered_owner.approval_status] || APPROVAL_STYLES.rejected).color
                              }}>
                                {flat.registered_owner.approval_status}
                              </span>
                            ) : (
                              <span className="text-xs" style={{ color: '#71717A' }}>Not registered</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => handleEditResident(flat.id)} data-testid={`save-edit-${flat.flat_number}`} style={{ backgroundColor: '#10B981', color: '#FFFFFF', fontSize: '12px', fontWeight: 'bold' }} className="h-7">
                                <Check size={12} className="mr-1" /> Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingResident(null)} className="h-7 text-xs">
                                <X size={12} />
                              </Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3" style={{ color: '#71717A' }}>{flat.building_name}</td>
                          <td className="p-3 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{flat.flat_number}</td>
                          <td className="p-3" style={{ color: '#09090B' }}>{flat.owner_name || '-'}</td>
                          <td className="p-3" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#71717A', fontSize: '12px' }}>{flat.contact_number || '-'}</td>
                          <td className="p-3">
                            <div className="flex gap-1 flex-wrap">
                              {flat.parking_spots.map(s => (
                                <span key={s} className="text-xs px-2 py-0.5" style={{ backgroundColor: '#F4F4F5', fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                                  {s}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">
                            {flat.registered_owner ? (
                              <span className="text-xs px-2 py-0.5" style={{
                                backgroundColor: (APPROVAL_STYLES[flat.registered_owner.approval_status] || APPROVAL_STYLES.rejected).bg,
                                color: (APPROVAL_STYLES[flat.registered_owner.approval_status] || APPROVAL_STYLES.rejected).color
                              }}>
                                {flat.registered_owner.approval_status}
                              </span>
                            ) : (
                              <span className="text-xs" style={{ color: '#71717A' }}>Not registered</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Button size="sm" variant="outline" onClick={() => {
                              setEditingResident(flat);
                              setEditForm({
                                owner_name: flat.owner_name || '',
                                flat_number: flat.flat_number || '',
                                building_name: flat.building_name || '',
                                contact_number: flat.contact_number || '',
                                parking_spots: flat.parking_spots.join('; ')
                              });
                            }} data-testid={`edit-btn-${flat.flat_number}`} className="h-7 text-xs">
                              <Edit2 size={12} className="mr-1" /> Edit
                            </Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredResidents.length === 0 && (
              <p className="text-center py-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                No residents found. Click "Add Resident" or upload a CSV to onboard residents.
              </p>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && hasSociety && (
          <BookingHistoryTab bookingHistory={bookingHistory} blockedVisitors={blockedVisitors} onBlockVisitor={handleBlockVisitor} />
        )}

        {/* Blocklist Tab */}
        {activeTab === 'blocklist' && hasSociety && (
          <BlocklistTab blockedVisitors={blockedVisitors} onBlockVisitor={handleBlockVisitor} onUnblock={handleUnblock} />
        )}

        {/* CSV Upload Tab */}
        {activeTab === 'upload' && hasSociety && (
          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
            <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
              Bulk Onboard Residents
            </h2>
            <p className="text-sm mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
              Upload a CSV or Excel file with building, flat, owner, and parking slot data.
            </p>

            {/* Format Guide */}
            <div className="p-4 mb-6 border" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }}>
              <p className="text-sm font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>Required Columns:</p>
              <div className="overflow-x-auto">
                <table className="text-xs w-full" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#E4E4E7' }}>
                      <th className="p-2 text-left">Tower/Building Name</th>
                      <th className="p-2 text-left">Flat Number</th>
                      <th className="p-2 text-left">Owner/Tenant Name</th>
                      <th className="p-2 text-left">Parking Slot(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t" style={{ borderColor: '#E4E4E7' }}>
                      <td className="p-2">Tower A</td>
                      <td className="p-2">A-101</td>
                      <td className="p-2">Rahul Sharma</td>
                      <td className="p-2">A-101-P1; A-101-P2</td>
                    </tr>
                    <tr className="border-t" style={{ borderColor: '#E4E4E7' }}>
                      <td className="p-2">Tower B</td>
                      <td className="p-2">B-201</td>
                      <td className="p-2">Priya Singh</td>
                      <td className="p-2">B-201-P1</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Multiple parking slots should be separated by semicolons (;)
              </p>
            </div>

            {/* Upload Area */}
            <div
              className="border-2 border-dashed p-8 text-center cursor-pointer transition-colors"
              style={{ borderColor: '#E4E4E7' }}
              onClick={() => fileRef.current?.click()}
              data-testid="csv-upload-area"
            >
              <Upload size={40} style={{ color: '#71717A', margin: '0 auto 12px' }} />
              <p className="font-bold mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                Click to upload CSV or Excel
              </p>
              <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                .csv, .xlsx supported
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={handleCSVUpload}
                data-testid="csv-file-input"
              />
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div className="mt-6 p-4 border" style={{ backgroundColor: '#D1FAE5', borderColor: '#10B981' }} data-testid="upload-result">
                <p className="font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#065F46' }}>Upload Successful</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#065F46' }}>{uploadResult.buildings_created}</p>
                    <p className="text-xs" style={{ color: '#065F46' }}>Buildings</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#065F46' }}>{uploadResult.flats_created}</p>
                    <p className="text-xs" style={{ color: '#065F46' }}>Flats</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#065F46' }}>{uploadResult.spots_created}</p>
                    <p className="text-xs" style={{ color: '#065F46' }}>Parking Spots</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Guards Tab */}
        {activeTab === 'guards' && hasSociety && (
          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                Security Guards
              </h2>
              <Button onClick={() => setShowCreateGuard(!showCreateGuard)} data-testid="create-guard-btn" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                <Plus size={16} className="mr-2" /> Add Guard
              </Button>
            </div>

            {showCreateGuard && (
              <div className="mb-6 p-4 border" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }} data-testid="create-guard-form">
                <p className="text-sm font-bold mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>New Guard Account</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Phone</Label>
                    <Input value={guardForm.phone} onChange={(e) => setGuardForm({ ...guardForm, phone: e.target.value })} placeholder="9876543210" data-testid="guard-phone-input" className="mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                  </div>
                  <div>
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Name</Label>
                    <Input value={guardForm.name} onChange={(e) => setGuardForm({ ...guardForm, name: e.target.value })} placeholder="Guard Name" data-testid="guard-name-input" className="mt-1" />
                  </div>
                  <div>
                    <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Password</Label>
                    <Input type="password" value={guardForm.password} onChange={(e) => setGuardForm({ ...guardForm, password: e.target.value })} placeholder="Password" data-testid="guard-password-input" className="mt-1" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreateGuard} data-testid="save-guard-btn" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                    Create Guard
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowCreateGuard(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {guards.length === 0 ? (
              <p className="text-center py-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                No guards assigned. Click "Add Guard" to create a guard account.
              </p>
            ) : (
              <div className="space-y-3">
                {guards.map((guard) => (
                  <div key={guard.id} className="flex items-center justify-between p-4 border" style={{ borderColor: '#E4E4E7' }} data-testid={`guard-item-${guard.phone}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#10B981' }}>
                        <Shield size={20} style={{ color: '#FFFFFF' }} />
                      </div>
                      <div>
                        <p className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>{guard.name}</p>
                        <p className="text-sm" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#71717A' }}>{guard.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>Active</span>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteGuard(guard.id, guard.name)} data-testid={`delete-guard-${guard.phone}`} className="h-7" style={{ borderColor: '#DC2626', color: '#DC2626' }}>
                        <Trash2 size={12} className="mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Approvals Tab */}
        {activeTab === 'approvals' && hasSociety && (
          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
            <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
              Pending Registration Requests
            </h2>
            {requests.length === 0 ? (
              <p className="text-center py-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                No pending requests
              </p>
            ) : (
              <div className="space-y-3">
                {requests.map((req) => (
                  <div key={req.id} className="flex items-center justify-between p-4 border" style={{ borderColor: '#E4E4E7' }} data-testid={`request-${req.id}`}>
                    <div>
                      <p className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>{req.name}</p>
                      <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                        Phone: <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{req.phone}</span>
                        {req.flat_number && <> | Flat: <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{req.flat_number}</span></>}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproval(req.id, 'approve')} data-testid={`approve-${req.id}`} style={{ backgroundColor: '#10B981', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                        <Check size={14} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleApproval(req.id, 'reject')} data-testid={`reject-${req.id}`} style={{ borderColor: '#DC2626', color: '#DC2626' }}>
                        <X size={14} className="mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Registration Link Tab */}
        {activeTab === 'registration' && hasSociety && regInfo && (
          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
            <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
              Owner Registration Link
            </h2>
            <p className="text-sm mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
              Share this link or QR code with parking owners for self-registration.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Link */}
              <div>
                <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Registration Link</Label>
                <div className="mt-2 p-3 border flex items-center gap-2" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }}>
                  <code className="flex-1 text-sm break-all" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }} data-testid="registration-link">
                    {regInfo.registration_link}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(regInfo.registration_link); toast.success('Link copied'); }}
                    data-testid="copy-link-btn"
                  >
                    Copy
                  </Button>
                </div>
                <div className="mt-4">
                  <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Society Code</Label>
                  <p className="mt-1 text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }} data-testid="society-code">
                    {regInfo.society_code}
                  </p>
                </div>
              </div>

              {/* QR Code */}
              <div className="text-center">
                <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>QR Code</Label>
                <div className="mt-2 p-4 border inline-block" style={{ borderColor: '#E4E4E7' }}>
                  <img src={regInfo.qr_code} alt="Registration QR" className="w-48 h-48" data-testid="registration-qr" />
                </div>
                <p className="text-xs mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                  Scan to open registration page
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadQR(regInfo.qr_code, `registration-qr-${regInfo.society_code}.png`)}
                  data-testid="download-registration-qr"
                  className="mt-2"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <Download size={14} className="mr-1" /> Download QR
                </Button>
              </div>
            </div>

            {/* Gate QR Section */}
            <div className="mt-10 pt-8" style={{ borderTop: '2px solid #E4E4E7' }}>
              <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                Visitor Gate QR Code
              </h2>
              <p className="text-sm mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Print and place this QR code at the society entry gate. Visitors scan it to self check-in, pay, and get a parking spot.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Gate Check-In Link</Label>
                  <div className="mt-2 p-3 border flex items-center gap-2" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }}>
                    <code className="flex-1 text-sm break-all" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }} data-testid="gate-checkin-link">
                      {regInfo.gate_checkin_link}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { navigator.clipboard.writeText(regInfo.gate_checkin_link); toast.success('Gate link copied'); }}
                      data-testid="copy-gate-link-btn"
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs mt-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                    Visitors scan → verify phone via OTP → enter vehicle details → pay → get spot allocated automatically.
                  </p>
                </div>

                <div className="text-center">
                  <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Gate QR Code</Label>
                  <div className="mt-2 p-4 border inline-block" style={{ borderColor: '#09090B', borderWidth: '2px' }}>
                    <img src={regInfo.gate_qr_code} alt="Gate Check-In QR" className="w-48 h-48" data-testid="gate-qr" />
                  </div>
                  <p className="text-xs mt-2 font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                    Print & place at entry gate
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadQR(regInfo.gate_qr_code, `entry-gate-qr-${regInfo.society_code}.png`)}
                    data-testid="download-entry-qr"
                    className="mt-2"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    <Download size={14} className="mr-1" /> Download QR
                  </Button>
                </div>
              </div>
            </div>

            {/* Exit Gate QR Section */}
            <div className="mt-10 pt-8" style={{ borderTop: '2px solid #E4E4E7' }}>
              <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                Visitor Exit Gate QR Code
              </h2>
              <p className="text-sm mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Place this QR at the exit gate. Visitors scan it to check out automatically. Guards can also check out vehicles manually from their dashboard.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Exit Gate Link</Label>
                  <div className="mt-2 p-3 border flex items-center gap-2" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }}>
                    <code className="flex-1 text-sm break-all" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }} data-testid="gate-exit-link">
                      {regInfo.gate_exit_link}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { navigator.clipboard.writeText(regInfo.gate_exit_link); toast.success('Exit link copied'); }}
                      data-testid="copy-exit-link-btn"
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs mt-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                    Visitors enter their vehicle number → instant checkout with overstay detection.
                  </p>
                </div>

                <div className="text-center">
                  <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Exit QR Code</Label>
                  <div className="mt-2 p-4 border inline-block" style={{ borderColor: '#DC2626', borderWidth: '2px' }}>
                    <img src={regInfo.gate_exit_qr_code} alt="Gate Exit QR" className="w-48 h-48" data-testid="gate-exit-qr" />
                  </div>
                  <p className="text-xs mt-2 font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#DC2626' }}>
                    Print & place at exit gate
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadQR(regInfo.gate_exit_qr_code, `exit-gate-qr-${regInfo.society_code}.png`)}
                    data-testid="download-exit-qr"
                    className="mt-2"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#DC2626', color: '#DC2626' }}
                  >
                    <Download size={14} className="mr-1" /> Download QR
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
